import { Chat, useChat } from "@ai-sdk/react";
import { toAISdkMessages } from "@mastra/ai-sdk/ui";
import {
  CHAT_TURN_TERMINAL_CHUNK_TYPES,
  getMastraThreadLangfuseSessionId,
} from "@repo/app-types/entities/copilot";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { checkIsAbortError } from "@repo/ui/helpers/errors";
import * as Sentry from "@sentry/react";
import {
  DefaultChatTransport,
  type ChatTransport,
  type UIMessage,
  type UIMessageChunk,
} from "ai";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { v4 as uuid } from "uuid";
import { z } from "zod";
import { useLazyMastraThreadMessagesQuery } from "../../../api/entities/mastraThread.js";
import { fetchWithSessionExpiry } from "../../../api/sessionExpiry.js";
import { getSessionAccessToken, mastraBaseUrl } from "../../../api/shared.js";

export interface MastraChatOptions {
  agentId: string;
  /** Local thread ID. Switching it resets local state and reloads history. */
  localThreadId?: string;
  /** Messages to pre-populate the chat with before any user interaction. */
  initialMessages?: UIMessage[];
  /**
   * Mastra thread to load history from. History reloads when `localThreadId`
   * changes; giving an existing local thread a remote counterpart (as happens
   * when the first message creates the thread) does not reload it.
   */
  loadThreadId?: string;
  /** Called with the thread when one of its turns ends, including a turn found running on load. */
  onTurnFinish?: (threadId: string) => void;
  /** Resolves per-send memory + request context (may create the thread). */
  onSend?: () => Promise<MastraChatSendInfo>;
}

export interface MastraChatSendInfo {
  /** Mastra memory thread. Omit for ephemeral (memory-less) chats. */
  thread?: { resourceId: string; threadId: string };
  /** Request context forwarded to the agent (e.g. playbookId). */
  requestContext?: Record<string, unknown>;
}

export interface MastraChat {
  messages: UIMessage[];
  /** Send a user text message and stream the agent's reply. */
  sendMessage: (text: string) => void;
  /**
   * Add messages without running the agent. A thread-backed chat sends them
   * with the next message, which saves them to the thread.
   */
  appendMessages: (messages: readonly UIMessage[]) => void;
  /** Abort the in-flight run. */
  stop: () => void;
  /** True while a run is submitting or streaming. */
  isRunning: boolean;
  /** True while loading thread history. */
  isLoading: boolean;
  setMessages: (
    messages: UIMessage[] | ((messages: UIMessage[]) => UIMessage[]),
  ) => void;
}

/**
 * Drives an admin agent chat through AI SDK UI's `useChat` against the backend
 * `chatRoute` (`POST {mastraBaseUrl}/chat/:agentId`). Replaces the
 * assistant-ui `useExternalStoreRuntime` + manual `@mastra/client-js`
 * streaming in `useMastraAgentRuntime`.
 */
export function useMastraChat(options: MastraChatOptions): MastraChat {
  const [defaultLocalThreadId] = useState(() => uuid());
  const { agentId, localThreadId = defaultLocalThreadId } = options;

  const [loadThreadMessages] = useLazyMastraThreadMessagesQuery();

  // The latest options, read by each thread's chat: its callbacks outlive the
  // render that created it.
  const latestRef = useRef<ThreadChatDependencies>({
    options,
    loadThreadMessages,
  });
  latestRef.current = { options, loadThreadMessages };

  const threadChat = useMemo(
    () => createThreadChat({ agentId, localThreadId, latestRef }),
    [agentId, localThreadId],
  );
  const { messages, status, setMessages } = useChat({
    chat: threadChat.chat,
  });

  // Load thread history whenever we switch threads.
  //
  // `isLoading` is derived rather than set from the effect: switching threads
  // swaps `useChat`'s state during the same render that changes
  // `localThreadId`, so a flag that only flips once the effect runs leaves one
  // painted frame with no messages and nothing loading — long enough for the
  // empty "new chat" state to flash between two existing threads.
  const { loadThreadId } = options;
  const [loadedLocalThreadId, setLoadedLocalThreadId] = useState<string>();
  const isLoading =
    loadThreadId !== undefined && loadedLocalThreadId !== localThreadId;
  // The chat is passed in rather than read here, so a superseded load writes
  // to the thread it loaded, not the one that replaced it.
  const onLoad = useEffectEvent(
    async (loadingChat: ThreadChat, signal: AbortSignal) => {
      try {
        if (loadThreadId === undefined) {
          return;
        }

        // Not `.unwrap()`: it rejects an aborted request with a plain `Error` that
        // `checkIsAbortError` can't tell from a real failure, whereas the promise
        // itself resolves and lets the abort check below run first.
        const request = loadThreadMessages({ agentId, threadId: loadThreadId });
        signal.addEventListener("abort", () => request.abort());
        const { data, error } = await request;

        if (signal.aborted) {
          return;
        }
        if (error || !data) {
          throw new Error(
            `Failed to load chat history: ${JSON.stringify(error)}`,
          );
        }

        // `toAISdkMessages` feeds a Mastra `MessageList`, which stamps `createdAt`
        // onto the messages it is handed. RTK Query's cache is frozen, so it has to
        // get a copy -- the pre-RTK fetch happened to pass freshly parsed JSON.
        loadingChat.chat.messages = toAISdkMessages(
          structuredClone(data.messages),
          { version: "v6" },
        );
        loadingChat
          .attachToRunningTurn(loadThreadId)
          .catch((error: unknown) => Sentry.captureException(error));
      } finally {
        // A superseded load must leave `loadedLocalThreadId` alone: it is what
        // holds `isLoading` false, and the effect only runs once per thread, so
        // stamping the abandoned thread's ID over the current one's would strand
        // the skeleton forever, and an aborted load still unwinds through here
        // after its replacement has started.
        //
        // Threads with no history to load still get marked, so `isLoading` stays
        // false once such a thread gains a remote counterpart of its own.
        if (!signal.aborted) {
          setLoadedLocalThreadId(localThreadId);
        }
      }
    },
  );

  useEffect(() => {
    const abortController = new AbortController();
    onLoad(threadChat, abortController.signal).catch((error: unknown) => {
      if (checkIsAbortError(error)) {
        return;
      }
      throw error;
    });
    return () => abortController.abort();
  }, [threadChat]);

  const sendTextMessage = useCallback(
    (text: string) => {
      threadChat.chat
        .sendMessage({ text })
        .catch((error: unknown) => Sentry.captureException(error));
    },
    [threadChat],
  );

  return {
    messages,
    sendMessage: sendTextMessage,
    appendMessages: threadChat.appendMessages,
    stop: threadChat.stop,
    isRunning: status === "submitted" || status === "streaming",
    isLoading,
    setMessages,
  };
}

interface ThreadChatDependencies {
  options: MastraChatOptions;
  loadThreadMessages: ReturnType<typeof useLazyMastraThreadMessagesQuery>[0];
}

/** One local thread's chat, with the operations the hook adds to it. */
interface ThreadChat {
  chat: Chat<UIMessage>;
  appendMessages: (messages: readonly UIMessage[]) => void;
  /** Reattach to the thread's turn if one is running, after loading history. */
  attachToRunningTurn: (threadId: string) => Promise<void>;
  /** Abort the in-flight run and cancel its turn server-side. */
  stop: () => void;
}

/**
 * How many times a turn's stream may end before the turn does (a dropped
 * connection, an agent-api deploy, a worker taking the turn over) and be
 * reattached, before the chat gives up and shows an error.
 */
const MAX_TURN_REATTACHES = 3;

/** Pause before a reattach after the first, so a lost network can return. */
const REATTACH_BACKOFF_MS = 1_000;

/**
 * Build one local thread's chat. Every callback acts on this thread's own
 * state, so a turn still streaming after the user switches threads settles in
 * the thread it started in.
 *
 * A thread-backed turn runs server-side under a claim, not the request, so
 * its stream can end before the turn does. One rule covers every case: a
 * stream that ends without a terminal chunk (`finish`, `error` or `abort`) is
 * reattached, which drops the partial reply and replays the turn from whichever
 * server answers.
 */
function createThreadChat({
  agentId,
  localThreadId,
  latestRef,
}: {
  agentId: string;
  localThreadId: string;
  latestRef: RefObject<ThreadChatDependencies>;
}): ThreadChat {
  // The Mastra thread this chat's turns run in: set by the first send, or by
  // loading history. Stopping and reattaching need it, since aborting the
  // fetch alone does not stop a thread-backed turn.
  let threadId: string | undefined;
  // Appended messages memory doesn't have yet; sent with each message until a
  // turn ends cleanly.
  const unsentMessageIds = new Set<string>();
  // Whether the stream read last delivered a terminal chunk.
  let hasTerminalChunk = false;
  // Settles the reattach in progress with how its request went; each
  // `resumeTurn` call sets its own.
  let settleReattach: ((outcome: ReattachOutcome) => void) | undefined;
  let reattachCount = 0;

  const watchForTerminalChunk = (stream: ReadableStream<UIMessageChunk>) =>
    stream.pipeThrough(
      new TransformStream<UIMessageChunk, UIMessageChunk>({
        transform(chunk, controller) {
          if (terminalChunkSchema.safeParse(chunk).success) {
            hasTerminalChunk = true;
          }
          controller.enqueue(chunk);
        },
      }),
    );

  const httpTransport = new DefaultChatTransport<UIMessage>({
    api: `${mastraBaseUrl}/chat/${agentId}`,
    fetch: fetchWithSessionExpiry,
    prepareSendMessagesRequest: async ({ messages }) => {
      const send = await latestRef.current.options.onSend?.();
      threadId = send?.thread?.threadId;
      reattachCount = 0;

      const memory = send?.thread
        ? { resource: send.thread.resourceId, thread: send.thread.threadId }
        : undefined;

      // With server memory the agent owns history, so only the latest user
      // message is sent, with any appended messages memory doesn't have yet;
      // memory-less agents send the full local list.
      const outgoing = memory
        ? messages.filter(
            (message, index) =>
              index === messages.length - 1 || unsentMessageIds.has(message.id),
          )
        : messages;

      const langfuseSessionId = getLangfuseSessionId({
        agentId,
        localThreadId,
        thread: send?.thread,
      });

      const token = await getSessionAccessToken();
      return {
        headers: { Authorization: `Bearer ${token}` },
        body: {
          messages: outgoing,
          ...(memory ? { memory } : {}),
          requestContext: {
            ...(langfuseSessionId ? { langfuseSessionId } : {}),
            ...send?.requestContext,
          },
        },
      };
    },
    // The thread's reattach endpoint replays the running turn's chunks and
    // tails it; 204 means nothing is in flight.
    prepareReconnectToStreamRequest: async ({ body }) => {
      const reattachThreadId = reattachRequestBodySchema.parse(body).threadId;
      const token = await getSessionAccessToken();
      return {
        api: `${mastraBaseUrl}/chat-turns/${reattachThreadId}/stream`,
        headers: { Authorization: `Bearer ${token}` },
      };
    },
  });

  const transport: ChatTransport<UIMessage> = {
    sendMessages: async (options) => {
      hasTerminalChunk = false;
      return watchForTerminalChunk(await httpTransport.sendMessages(options));
    },
    reconnectToStream: async (options) => {
      const settle = settleReattach;
      settleReattach = undefined;
      hasTerminalChunk = false;
      const stream = await httpTransport.reconnectToStream(options);
      settle?.(stream ? "streamed" : "no-turn");
      return stream && watchForTerminalChunk(stream);
    },
  };

  // Reattach to the thread's running turn and report how the request went; a
  // stream that opened is consumed before this settles.
  const resumeTurn = async (turnThreadId: string): Promise<ReattachOutcome> => {
    let outcome: ReattachOutcome = "failed";
    settleReattach = (settled) => {
      outcome = settled;
    };
    await chat.resumeStream({ body: { threadId: turnThreadId } });
    return outcome;
  };

  const finishTurn = () => {
    if (threadId) {
      latestRef.current.options.onTurnFinish?.(threadId);
    }
  };

  // On a send/stream failure AI SDK only flips `status` to "error"; the chat
  // surfaces don't render that, so (matching the previous runtime) append a
  // visible assistant error message. The user's prompt is preserved, so they
  // can retry by resending.
  const appendErrorMessage = () => {
    chat.messages = [
      ...chat.messages,
      {
        id: `error-${uuid()}`,
        role: "assistant",
        parts: [{ type: "text", text: "Something went wrong." }],
      },
    ];
  };

  // The turn died: keep its partial reply on screen under the error.
  const failInterruptedTurn = (partialMessage: UIMessage) => {
    if (
      partialMessage.parts.length > 0 &&
      chat.lastMessage?.id !== partialMessage.id
    ) {
      chat.messages = [...chat.messages, partialMessage];
    }
    appendErrorMessage();
    finishTurn();
  };

  // Called after a reattach finds no turn in flight. That means one of two
  // things: the turn finished while we were disconnected, or it died. A
  // finished turn's reply is saved on the thread, so the saved history tells
  // the two apart.
  //
  // Returns the saved messages if the turn finished, otherwise null.
  const loadSavedMessagesIfTurnFinished = async (
    endedTurnThreadId: string,
  ): Promise<UIMessage[] | null> => {
    const { data } = await latestRef.current.loadThreadMessages({
      agentId,
      threadId: endedTurnThreadId,
    });
    if (!data) {
      return null;
    }
    const savedMessages: UIMessage[] = toAISdkMessages(
      structuredClone(data.messages),
      { version: "v6" },
    );

    // Counting prompts, because a saved message does not carry the id the
    // client gave it, so there is nothing to match ours against. Erring toward
    // "died" is the safe direction: it shows the error and keeps the reply on
    // screen, where erring the other way would replace a live chat with stale
    // history.
    const isPromptSaved =
      countUserMessages(savedMessages) >= countUserMessages(chat.messages);
    const endsWithReply = savedMessages.at(-1)?.role === "assistant";
    return isPromptSaved && endsWithReply ? savedMessages : null;
  };

  // Reattach to a turn whose stream ended before it did. The relay replays the
  // turn from its first chunk, so the partial reply is removed first;
  // otherwise the replay would append onto it and duplicate its parts.
  const reattachInterruptedTurn = async (
    turnThreadId: string,
    partialMessage: UIMessage,
  ) => {
    if (reattachCount >= MAX_TURN_REATTACHES) {
      failInterruptedTurn(partialMessage);
      return;
    }
    if (reattachCount > 0) {
      await new Promise((resolve) =>
        setTimeout(resolve, REATTACH_BACKOFF_MS * reattachCount),
      );
    }
    reattachCount += 1;
    if (chat.lastMessage?.id === partialMessage.id) {
      chat.messages = chat.messages.slice(0, -1);
    }
    const outcome = await resumeTurn(turnThreadId);
    switch (outcome) {
      case "streamed":
        // `onFinish` settles the turn however its stream ended.
        return;
      case "failed":
        await reattachInterruptedTurn(turnThreadId, partialMessage);
        return;
      case "no-turn": {
        const finishedTurnMessages =
          await loadSavedMessagesIfTurnFinished(turnThreadId);
        if (finishedTurnMessages) {
          chat.messages = finishedTurnMessages;
          finishTurn();
        } else {
          failInterruptedTurn(partialMessage);
        }
        return;
      }
      default:
        assertUnreachable(outcome);
    }
  };

  const chat: Chat<UIMessage> = new Chat<UIMessage>({
    id: localThreadId,
    messages: latestRef.current.options.initialMessages,
    transport,
    onFinish: ({ message, isAbort, isDisconnect, isError }) => {
      // A stream cut short: the connection dropped, or the server closed it
      // for the client to reattach elsewhere. A refused request is not one.
      const wasInterrupted =
        !isAbort && !hasTerminalChunk && (!isError || isDisconnect);
      if (wasInterrupted && threadId) {
        reattachInterruptedTurn(threadId, message).catch((error: unknown) =>
          Sentry.captureException(error),
        );
        return;
      }
      if (isError) {
        appendErrorMessage();
      } else if (!isAbort) {
        unsentMessageIds.clear();
      }
      finishTurn();
    },
  });

  return {
    chat,
    appendMessages: (messages) => {
      messages.forEach((message) => unsentMessageIds.add(message.id));
      chat.messages = [...chat.messages, ...messages];
    },
    attachToRunningTurn: async (loadedThreadId) => {
      threadId = loadedThreadId;
      reattachCount = 0;
      const outcome = await resumeTurn(loadedThreadId);
      // A 204 is a silent no-op: the history just loaded is the whole thread.
      if (outcome === "failed") {
        appendErrorMessage();
      }
    },
    // Aborting the fetch only disconnects the client; a thread-backed run keeps
    // going server-side until the cancel endpoint deletes its active-turn
    // pointer. Best-effort: a failed cancel just lets the turn run to
    // completion.
    stop: () => {
      chat.stop().catch((error: unknown) => Sentry.captureException(error));
      if (threadId) {
        cancelChatTurn(threadId).catch((error: unknown) =>
          Sentry.captureException(error),
        );
      }
    },
  };
}

/** How a reattach request went: `no-turn` is the relay's 204. */
type ReattachOutcome = "streamed" | "no-turn" | "failed";

const reattachRequestBodySchema = z.object({ threadId: z.string() });

/** A chunk that ends the assistant turn: it finished, failed or was stopped. */
const terminalChunkSchema = z.object({
  type: z.enum(CHAT_TURN_TERMINAL_CHUNK_TYPES),
});

function countUserMessages(messages: readonly UIMessage[]): number {
  return messages.filter((message) => message.role === "user").length;
}

async function cancelChatTurn(threadId: string): Promise<void> {
  const token = await getSessionAccessToken();
  await fetch(`${mastraBaseUrl}/chat-turns/${threadId}/cancel`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
}

function getLangfuseSessionId(options: {
  agentId: string;
  localThreadId: string | undefined;
  thread: { threadId: string } | undefined;
}) {
  if (options.thread) {
    return getMastraThreadLangfuseSessionId(
      options.agentId,
      options.thread.threadId,
    );
  }
  if (options.localThreadId) {
    return `${options.agentId}_client_thread_${options.localThreadId}`;
  }
  return undefined;
}
